import React, { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Switch } from "@/components/ui/switch.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import {
  Trash2, Plus, Scissors, Download, LogIn, LogOut, Save,
  FolderOpen, Package, ArchiveRestore, Maximize2, ArrowLeft, Hand, RotateCcw,
} from "lucide-react";
import { KioskView } from "./kiosk-view.tsx";
import { PRESETS } from "./presets.ts";
import { t, type Lang } from "./i18n.ts";
import { SheetCanvas } from "./sheet-canvas.tsx";
import { InteractiveSheetCanvas } from "./interactive-sheet-canvas.tsx";
import { generatePDF } from "./pdf-export.ts";
import { useAuth, useStock, useProjects } from "./hooks/use-api.ts";
import type {
  StockSheet,
  Piece,
  Placement,
  MaterialPreset,
  OptimizeResult,
} from "./types.ts";

// Load WASM engine at runtime (avoid bundler resolving this)
let optimizeWasm: ((input: string) => string) | null = null;

async function loadEngine() {
  const url = new URL("/cutoptim_engine.js", window.location.origin).href;
  const mod = await import(/* @vite-ignore */ url);
  await mod.default();
  optimizeWasm = mod.optimize_wasm;
}

const engineReady = loadEngine();

interface AppProps {
  lang: Lang;
  setLang: (l: Lang) => void;
  onBack: () => void;
}

export function App({ lang, setLang, onBack }: AppProps) {
  const [preset, setPreset] = useState<MaterialPreset>("wood");
  const [stocks, setStocks] = useState<StockSheet[]>([
    ...PRESETS.wood.default_stocks,
  ]);
  const [pieces, setPieces] = useState<Piece[]>([
    { width: 800, height: 300, quantity: 4, label: "Półka / Shelf", no_rotate: false },
    { width: 800, height: 600, quantity: 2, label: "Bok / Side", no_rotate: false },
    { width: 800, height: 800, quantity: 1, label: "Tył / Back", no_rotate: false },
    { width: 800, height: 300, quantity: 1, label: "Góra / Top", no_rotate: false },
    { width: 360, height: 300, quantity: 2, label: "Przegroda / Divider", no_rotate: false },
    { width: 400, height: 150, quantity: 4, label: "Szuflada / Drawer front", no_rotate: true },
  ]);
  const [kerf, setKerf] = useState(PRESETS.wood.default_kerf);
  const [allowRotation, setAllowRotation] = useState(PRESETS.wood.allow_rotation);
  const [result, setResult] = useState<OptimizeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  // Auth
  const { user, loading: authLoading, signup, login, logout } = useAuth();
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [authEmail, setAuthEmail] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authError, setAuthError] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  // Stock inventory
  const { items: stockItems, addSheets: addStockSheets, removeSheet: removeStockSheet } = useStock(user);

  // Projects
  const { projects, saveProject, loadProject, deleteProject } = useProjects(user);
  const [projectName, setProjectName] = useState("");
  const [currentProjectId, setCurrentProjectId] = useState<number | null>(null);
  const [showProjects, setShowProjects] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // Kiosk view
  const [kioskIndex, setKioskIndex] = useState<number | null>(null);

  // Interactive editing
  const [editingSheet, setEditingSheet] = useState<number | null>(null);
  const originalResultRef = useRef<OptimizeResult | null>(null);

  const switchPreset = useCallback((p: MaterialPreset) => {
    setPreset(p);
    setStocks([...PRESETS[p].default_stocks]);
    setKerf(PRESETS[p].default_kerf);
    setAllowRotation(PRESETS[p].allow_rotation);
    setResult(null);
  }, []);

  const addStock = useCallback(() => {
    setStocks((s) => [...s, { width: 2800, height: 2070, label: "", cost: 0 }]);
  }, []);

  const updateStock = useCallback((i: number, field: keyof StockSheet, val: string) => {
    setStocks((s) => {
      const next = [...s];
      const sheet = { ...next[i]! };
      if (field === "label") sheet.label = val;
      else sheet[field] = parseFloat(val) || 0;
      next[i] = sheet;
      return next;
    });
  }, []);

  const removeStock = useCallback((i: number) => {
    setStocks((s) => s.filter((_, idx) => idx !== i));
  }, []);

  const addPiece = useCallback(() => {
    setPieces((p) => [
      ...p,
      { width: 500, height: 300, quantity: 1, label: "", no_rotate: false },
    ]);
  }, []);

  const updatePiece = useCallback((i: number, field: keyof Piece, val: string | boolean) => {
    setPieces((p) => {
      const next = [...p];
      const piece = { ...next[i]! };
      if (field === "label") piece.label = val as string;
      else if (field === "no_rotate") piece.no_rotate = val as boolean;
      else piece[field] = parseFloat(val as string) || 0;
      next[i] = piece;
      return next;
    });
  }, []);

  const removePiece = useCallback((i: number) => {
    setPieces((p) => p.filter((_, idx) => idx !== i));
  }, []);

  const runOptimize = useCallback(async () => {
    setLoading(true);
    await engineReady;
    if (!optimizeWasm) return;

    const input = JSON.stringify({
      stock_sheets: stocks,
      pieces,
      config: { kerf, allow_rotation: allowRotation },
    });

    const res = JSON.parse(optimizeWasm(input)) as OptimizeResult;
    setResult(res);
    setEditingSheet(null);
    originalResultRef.current = null;
    setLoading(false);

    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, [stocks, pieces, kerf, allowRotation]);

  // Auth submit
  const handleAuth = useCallback(async () => {
    setAuthError("");
    try {
      if (authMode === "signup") await signup(authEmail, authPass);
      else await login(authEmail, authPass);
      setShowAuth(false);
      setAuthEmail("");
      setAuthPass("");
    } catch (e: unknown) {
      setAuthError((e as Error).message);
    }
  }, [authMode, authEmail, authPass, signup, login]);

  // Save remnant to stock
  const saveRemnant = useCallback(
    async (width: number, height: number, material: string) => {
      await addStockSheets([
        { width, height, label: `${Math.round(width)}×${Math.round(height)}`, material, is_remnant: true },
      ]);
    },
    [addStockSheets],
  );

  // Load stock item into current stocks list
  const useFromStock = useCallback((item: { width: number; height: number; label: string; cost: number }) => {
    setStocks((s) => [...s, { width: item.width, height: item.height, label: item.label, cost: item.cost }]);
  }, []);

  // Toggle interactive editing on a sheet
  const toggleEditSheet = useCallback((sheetIndex: number) => {
    if (editingSheet === sheetIndex) {
      setEditingSheet(null);
    } else {
      if (!originalResultRef.current && result) {
        originalResultRef.current = JSON.parse(JSON.stringify(result));
      }
      setEditingSheet(sheetIndex);
    }
  }, [editingSheet, result]);

  // Reset sheet to original algorithm layout
  const resetSheetLayout = useCallback((sheetIndex: number) => {
    if (!originalResultRef.current || !result) return;
    const original = originalResultRef.current.sheets[sheetIndex];
    if (!original) return;
    setResult((prev) => {
      if (!prev) return prev;
      const next = { ...prev, sheets: [...prev.sheets] };
      next.sheets[sheetIndex] = { ...next.sheets[sheetIndex]!, placements: [...original.placements] };
      return next;
    });
  }, [result]);

  // Update placements from interactive editor
  const handlePlacementsChange = useCallback((sheetIndex: number, placements: Placement[]) => {
    setResult((prev) => {
      if (!prev) return prev;
      const next = { ...prev, sheets: [...prev.sheets] };
      next.sheets[sheetIndex] = { ...next.sheets[sheetIndex]!, placements };
      return next;
    });
  }, []);

  // Save project
  const handleSaveProject = useCallback(async () => {
    if (!projectName.trim()) return;
    const data = { stocks, pieces, kerf, allowRotation, preset };
    const id = await saveProject(projectName, data, currentProjectId ?? undefined);
    if (id) setCurrentProjectId(id);
    setSaveMsg(t("saved", lang));
    setTimeout(() => setSaveMsg(""), 2000);
  }, [projectName, stocks, pieces, kerf, allowRotation, preset, currentProjectId, saveProject, lang]);

  // Load project
  const handleLoadProject = useCallback(
    async (id: number) => {
      const proj = await loadProject(id);
      const d = proj.data as {
        stocks: StockSheet[];
        pieces: Piece[];
        kerf: number;
        allowRotation: boolean;
        preset: MaterialPreset;
      };
      setStocks(d.stocks);
      setPieces(d.pieces);
      setKerf(d.kerf);
      setAllowRotation(d.allowRotation);
      setPreset(d.preset);
      setProjectName(proj.name);
      setCurrentProjectId(proj.id);
      setShowProjects(false);
      setResult(null);
    },
    [loadProject],
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Scissors className="h-6 w-6" />
                {t("title", lang)}
              </h1>
              <p className="text-muted-foreground text-sm">{t("subtitle", lang)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 mr-2">
              <Button variant={lang === "pl" ? "default" : "outline"} size="sm" onClick={() => setLang("pl")}>
                PL
              </Button>
              <Button variant={lang === "en" ? "default" : "outline"} size="sm" onClick={() => setLang("en")}>
                EN
              </Button>
            </div>
            {authLoading ? null : user ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{user.email}</span>
                <Button variant="ghost" size="sm" onClick={logout}>
                  <LogOut className="h-4 w-4 mr-1" />
                  {t("logout", lang)}
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowAuth(true)}>
                <LogIn className="h-4 w-4 mr-1" />
                {t("login", lang)}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Auth modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowAuth(false)}>
          <Card className="w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>{authMode === "login" ? t("login", lang) : t("signup", lang)}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("email", lang)}</Label>
                <Input
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAuth()}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("password", lang)}</Label>
                <Input
                  type="password"
                  value={authPass}
                  onChange={(e) => setAuthPass(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAuth()}
                />
              </div>
              {authError && <p className="text-sm text-red-600">{authError}</p>}
              <Button className="w-full" onClick={handleAuth}>
                {authMode === "login" ? t("login", lang) : t("signup", lang)}
              </Button>
              <p className="text-sm text-center text-muted-foreground">
                {authMode === "login" ? t("no_account", lang) : t("has_account", lang)}{" "}
                <button
                  className="text-primary underline"
                  onClick={() => setAuthMode(authMode === "login" ? "signup" : "login")}
                >
                  {authMode === "login" ? t("signup", lang) : t("login", lang)}
                </button>
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <main className="mx-auto max-w-6xl px-4 py-6 space-y-6">
        {/* Material Presets */}
        <div className="flex flex-wrap gap-2">
          {(Object.keys(PRESETS) as MaterialPreset[]).map((p) => (
            <Button key={p} variant={preset === p ? "default" : "outline"} onClick={() => switchPreset(p)} className="text-base">
              {PRESETS[p].icon} {lang === "pl" ? PRESETS[p].label_pl : PRESETS[p].label_en}
            </Button>
          ))}
        </div>

        {/* Project bar */}
        {user && (
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder={t("project_name", lang)}
                  className="h-9 max-w-xs"
                />
                <Button variant="outline" size="sm" onClick={handleSaveProject} disabled={!projectName.trim()}>
                  <Save className="h-4 w-4 mr-1" />
                  {t("save_project", lang)}
                </Button>
                {saveMsg && <span className="text-sm text-green-600">{saveMsg}</span>}
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={() => setShowProjects(!showProjects)}>
                  <FolderOpen className="h-4 w-4 mr-1" />
                  {t("my_projects", lang)}
                </Button>
              </div>
              {showProjects && projects.length > 0 && (
                <div className="mt-3 space-y-1">
                  {projects.map((proj) => (
                    <div key={proj.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted">
                      <button className="flex-1 text-left text-sm" onClick={() => handleLoadProject(proj.id)}>
                        {proj.name}
                      </button>
                      <span className="text-xs text-muted-foreground">{new Date(proj.updated_at).toLocaleDateString()}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteProject(proj.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stock Sheets */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t("stock_sheets", lang)}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {stocks.map((s, i) => (
                <div key={i} className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-muted-foreground">{t("width", lang)}</Label>
                    <Input type="number" value={s.width || ""} onChange={(e) => updateStock(i, "width", e.target.value)} className="h-9" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-muted-foreground">{t("height", lang)}</Label>
                    <Input type="number" value={s.height || ""} onChange={(e) => updateStock(i, "height", e.target.value)} className="h-9" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-muted-foreground">{t("label", lang)}</Label>
                    <Input value={s.label} onChange={(e) => updateStock(i, "label", e.target.value)} className="h-9" placeholder="np. Egger W1000" />
                  </div>
                  <div className="w-20 space-y-1">
                    <Label className="text-xs text-muted-foreground">{t("cost", lang)}</Label>
                    <Input type="number" value={s.cost || ""} onChange={(e) => updateStock(i, "cost", e.target.value)} className="h-9" />
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeStock(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={addStock}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t("add_stock", lang)}
                </Button>
              </div>

              {/* Stock inventory (logged in users) */}
              {user && stockItems.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium flex items-center gap-1">
                      <Package className="h-4 w-4" />
                      {t("stock_inventory", lang)}
                    </p>
                    {stockItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-2 text-sm py-1 px-2 rounded bg-muted/50">
                        <span className="flex-1">
                          {item.label || `${item.width}×${item.height}`}
                          <span className="text-muted-foreground ml-1">
                            {item.width}×{item.height} mm
                          </span>
                          {item.is_remnant ? (
                            <Badge variant="secondary" className="ml-1 text-xs">{t("remnant_badge", lang)}</Badge>
                          ) : null}
                        </span>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => useFromStock(item)}>
                          <ArchiveRestore className="h-3 w-3 mr-1" />
                          {t("use_from_stock", lang)}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeStockSheet(item.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Pieces */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t("required_pieces", lang)}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pieces.map((p, i) => (
                <div key={i} className="flex gap-2 items-end">
                  <div className="w-20 space-y-1">
                    <Label className="text-xs text-muted-foreground">{t("width", lang)}</Label>
                    <Input type="number" value={p.width || ""} onChange={(e) => updatePiece(i, "width", e.target.value)} className="h-9" />
                  </div>
                  <div className="w-20 space-y-1">
                    <Label className="text-xs text-muted-foreground">{t("height", lang)}</Label>
                    <Input type="number" value={p.height || ""} onChange={(e) => updatePiece(i, "height", e.target.value)} className="h-9" />
                  </div>
                  <div className="w-16 space-y-1">
                    <Label className="text-xs text-muted-foreground">{t("quantity", lang)}</Label>
                    <Input type="number" value={p.quantity || ""} onChange={(e) => updatePiece(i, "quantity", e.target.value)} className="h-9" min={1} />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-muted-foreground">{t("label", lang)}</Label>
                    <Input value={p.label} onChange={(e) => updatePiece(i, "label", e.target.value)} className="h-9" />
                  </div>
                  <div className="flex items-center gap-1 pb-0.5">
                    <Switch checked={p.no_rotate} onCheckedChange={(v) => updatePiece(i, "no_rotate", v)} />
                    <Label className="text-xs whitespace-nowrap">{t("no_rotate", lang)}</Label>
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removePiece(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addPiece}>
                <Plus className="h-4 w-4 mr-1" />
                {t("add_piece", lang)}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Settings + Optimize */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Label>{t("kerf", lang)} (mm):</Label>
                <Input
                  type="number"
                  value={kerf}
                  onChange={(e) => setKerf(parseFloat(e.target.value) || 0)}
                  className="w-20 h-9"
                  step={0.5}
                  min={0}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={allowRotation} onCheckedChange={setAllowRotation} />
                <Label>{t("allow_rotation", lang)}</Label>
              </div>
              <div className="flex-1" />
              <Button
                size="lg"
                onClick={runOptimize}
                disabled={loading || stocks.length === 0 || pieces.length === 0}
                className="text-base px-8"
              >
                <Scissors className="h-5 w-5 mr-2" />
                {loading ? "..." : t("optimize", lang)}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {result && !result.error && (
          <div ref={resultRef} className="space-y-6">
            <Separator />

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold">{result.total_sheets}</div>
                  <div className="text-sm text-muted-foreground mt-1">{t("sheets_used", lang)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <div
                    className={`text-4xl font-bold ${
                      result.total_waste_percent < 15
                        ? "text-green-600"
                        : result.total_waste_percent < 30
                          ? "text-yellow-600"
                          : "text-red-600"
                    }`}
                  >
                    {result.total_waste_percent.toFixed(1)}%
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{t("waste", lang)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold">{result.total_cost > 0 ? result.total_cost.toFixed(2) : "—"}</div>
                  <div className="text-sm text-muted-foreground mt-1">{t("total_cost", lang)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Unplaced warning */}
            {result.unplaced.length > 0 && (
              <Card className="border-red-300 bg-red-50">
                <CardContent className="pt-6">
                  <p className="font-semibold text-red-800 mb-2">{t("unplaced_warning", lang)}:</p>
                  <ul className="list-disc pl-5 text-red-700 text-sm">
                    {result.unplaced.map((u, i) => (
                      <li key={i}>
                        {u.label} ({u.width}×{u.height}) — {u.reason}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Sheet layouts */}
            {result.sheets.map((sheet, i) => (
              <Card key={i}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    {t("sheet_nr", lang)} {i + 1}
                    {sheet.label && <Badge variant="secondary">{sheet.label}</Badge>}
                    <Badge
                      variant="outline"
                      className={
                        sheet.waste_percent < 15
                          ? "text-green-700 border-green-300"
                          : sheet.waste_percent < 30
                            ? "text-yellow-700 border-yellow-300"
                            : "text-red-700 border-red-300"
                      }
                    >
                      {sheet.waste_percent.toFixed(1)}% {t("waste", lang)}
                    </Badge>
                    <span className="text-sm text-muted-foreground font-normal">
                      {sheet.width}×{sheet.height} mm
                    </span>
                    <div className="flex-1" />
                    <Button
                      variant={editingSheet === i ? "default" : "ghost"}
                      size="sm"
                      className="h-8 text-xs gap-1"
                      onClick={() => toggleEditSheet(i)}
                    >
                      <Hand className="h-3.5 w-3.5" />
                      {t("try_manual", lang)}
                    </Button>
                    {editingSheet === i && (
                      <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => resetSheetLayout(i)}>
                        <RotateCcw className="h-3.5 w-3.5" />
                        {t("reset_layout", lang)}
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setKioskIndex(i)}>
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editingSheet === i ? (
                    <>
                      <p className="text-xs text-muted-foreground mb-2">{t("editing_layout", lang)}</p>
                      <InteractiveSheetCanvas
                        sheet={sheet}
                        lang={lang}
                        onPlacementsChange={(p) => handlePlacementsChange(i, p)}
                      />
                    </>
                  ) : (
                    <SheetCanvas sheet={sheet} lang={lang} />
                  )}
                  {/* Save remnants to stock */}
                  {user && sheet.remnants.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {sheet.remnants.map((r, ri) => (
                        <Button
                          key={ri}
                          variant="outline"
                          size="sm"
                          className="text-xs text-green-700 border-green-300 hover:bg-green-50"
                          onClick={() => saveRemnant(r.width, r.height, preset)}
                        >
                          <Package className="h-3 w-3 mr-1" />
                          {t("save_remnant", lang)} {Math.round(r.width)}×{Math.round(r.height)}
                        </Button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Export */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => void generatePDF(result, lang)}>
                <Download className="h-4 w-4 mr-2" />
                {t("export_pdf", lang)}
              </Button>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t mt-12 py-6 text-center text-sm text-muted-foreground">
        HolySheet &copy; 2026 — {t("footer", lang)}
      </footer>

      {/* Kiosk view */}
      {kioskIndex !== null && result && (
        <KioskView
          sheets={result.sheets}
          index={kioskIndex}
          lang={lang}
          onClose={() => setKioskIndex(null)}
          onNavigate={setKioskIndex}
        />
      )}
    </div>
  );
}
